import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { SidebarModule } from 'primeng/sidebar';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { forkJoin } from 'rxjs';
import { saveAs } from 'file-saver';
import { TerceroUPPasarelaAdjunto } from '../../../../_models/tercero-up-pasarela-adjunto';
import { TerceroUpPasarelaAdjuntoService } from '../../../../_services/tercero-up-pasarela-adjunto.service';
import { AdjuntoService } from '../../../../_services/adjunto.service';
import { NavService } from '../../../../_services/_nav.service';

@Component({
  selector: 'app-matrix-visor-adjunto-pasarela',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SidebarModule,
    DropdownModule,
    ButtonModule,
  ],
  templateUrl: './matrix-visor-adjunto-pasarela.component.html',
  styleUrls: ['./matrix-visor-adjunto-pasarela.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MatrixVisorAdjuntoPasarelaComponent implements OnInit {

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() idTerceroUPPasarela: string = '';

  private _adjunto: any = null;

  @Input()
  set adjunto(item: any) {
    this._adjunto = item;
    if (item && item.idAdjunto) {
      this.cargarArchivo(item);
    } else {
      this.limpiar();
    }
  }

  get adjunto(): any {
    return this._adjunto;
  }

  @Output() onCalificado = new EventEmitter<{ adjunto: any; indicadorCalificacion: boolean }>();

  zoom: number = 100;
  opcionesZoom = [
    { label: '25%', value: 25 },
    { label: '50%', value: 50 },
    { label: '75%', value: 75 },
    { label: '100%', value: 100 },
    { label: '125%', value: 125 },
    { label: '150%', value: 150 },
    { label: '200%', value: 200 },
  ];

  loadingFile: boolean = false;
  guardandoCalificacion: boolean = false;
  archivoBlob: Blob | null = null;
  fileUrl: SafeResourceUrl | null = null;
  nombreArchivo: string = '';
  usuarioCarga: string = '';
  esPdf: boolean = false;

  constructor(
    private adjService: AdjuntoService,
    private terceroUpPasarelaAdjuntoService: TerceroUpPasarelaAdjuntoService,
    private navService: NavService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {}

  /**
   * Descarga el archivo del servidor y prepara su previsualización
   */
  cargarArchivo(adjunto: any): void {
    if (!adjunto?.idAdjunto) {
      return;
    }
    this.loadingFile = true;
    forkJoin([
      this.adjService.descargarArchivo(adjunto.idAdjunto, adjunto.indicadorTercero),
      this.adjService.obtenerAdjunto(adjunto.idAdjunto, adjunto.indicadorTercero),
    ]).subscribe({
      next: ([archivo, infoAdjunto]) => {
        this.archivoBlob = archivo;
        this.nombreArchivo = infoAdjunto?.nombreArchivo || adjunto.nombreAdjunto || adjunto.nombre || 'documento.pdf';
        this.usuarioCarga = infoAdjunto?.auditoriaUsuario || adjunto.auditoriaUsuario || '';
        if (this.adjunto && !this.adjunto.fechaAdjunto && (infoAdjunto?.auditoriaFecha || (infoAdjunto as any)?.fechaAdjunto)) {
          this.adjunto.fechaAdjunto = infoAdjunto.auditoriaFecha || (infoAdjunto as any).fechaAdjunto;
        }
        const tipoMime = archivo?.type || (infoAdjunto as any)?.tipoArchivo || '';
        this.esPdf = tipoMime.includes('pdf') || this.nombreArchivo.toLowerCase().endsWith('.pdf');

        const objectUrl = URL.createObjectURL(archivo);
        this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
        this.loadingFile = false;
      },
      error: () => {
        this.loadingFile = false;
        this.navService.setMesage(3);
      },
    });
  }

  /**
   * Descarga el archivo actualmente cargado a disco
   */
  descargar(): void {
    if (!this.archivoBlob) {
      return;
    }
    saveAs(this.archivoBlob, this.nombreArchivo || 'documento');
  }

  /**
   * Actualiza el indicadorCalificacion (true = aprobado, false = rechazado)
   */
  calificar(aprobado: boolean): void {
    if (!this.adjunto || this.guardandoCalificacion) {
      return;
    }

    this.guardandoCalificacion = true;
    const payload: Partial<TerceroUPPasarelaAdjunto> & { indicadorHabilitado?: boolean } = {
      idTerceroUPPasarela: this.idTerceroUPPasarela || this.adjunto.idTerceroUPPasarela,
      idTerceroUPPasarelaAdjunto: this.adjunto.idTerceroUPPasarelaAdjunto,
      idTercerosUPPasarelasAdjuntosVersion: this.adjunto.idTercerosUPPasarelasAdjuntosVersion,
      idDocumentoTercero: this.adjunto.idDocumentoTercero,
      codigo: this.adjunto.codigo,
      nombre: this.adjunto.nombre || this.adjunto.nombreRespaldo,
      codigoTipoVigencia: this.adjunto.codigoTipoVigencia,
      tipoVigencia: this.adjunto.tipoVigencia,
      indicadorSolicitarCliente: this.adjunto.indicadorSolicitarCliente,
      orden: this.adjunto.orden || 0,
      idAdjunto: this.adjunto.idAdjunto,
      indicadorHabilitado: true,
      indicadorCalificacion: aprobado,
    };

    this.terceroUpPasarelaAdjuntoService.crearAdjuntoVersion(payload).subscribe({
      next: () => {
        this.adjunto.indicadorCalificacion = aprobado;
        this.navService.setMesage(2);
        this.guardandoCalificacion = false;
        this.onCalificado.emit({ adjunto: this.adjunto, indicadorCalificacion: aprobado });
      },
      error: () => {
        this.guardandoCalificacion = false;
        this.navService.setMesage(3);
      },
    });
  }

  /**
   * Retorna el texto del badge de calificación en el visor
   */
  get estadoCalificacionTexto(): string {
    if (this.adjunto?.indicadorCalificacion === true) {
      return this.navService.buscarMensaje('MATRIXADJUNTOSMODAL.LBL_APROBADO');
    }
    if (this.adjunto?.indicadorCalificacion === false) {
      return this.navService.buscarMensaje('MATRIXADJUNTOSMODAL.LBL_RECHAZADO');
    }
    return this.navService.buscarMensaje('MATRIXADJUNTOSMODAL.LBL_VERIFICABLE');
  }

  /**
   * Retorna la clase CSS del badge de calificación en el visor
   */
  get estadoCalificacionClase(): string {
    if (this.adjunto?.indicadorCalificacion === true) {
      return 'badge-visor-aprobado';
    }
    if (this.adjunto?.indicadorCalificacion === false) {
      return 'badge-visor-rechazado';
    }
    return 'badge-visor-pendiente';
  }

  /**
   * Formatea la fecha al formato visual '15 sep 2026'
   */
  formatearFecha(fecha: any): string {
    if (!fecha) {
      return '--/--/----';
    }
    if (typeof fecha === 'string') {
      const mesesEspanol = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
      const contieneMes = mesesEspanol.some(m => fecha.toLowerCase().includes(m));
      if (contieneMes) {
        return fecha;
      }
    }
    try {
      const d = new Date(fecha);
      if (!isNaN(d.getTime())) {
        const dia = d.getDate().toString().padStart(2, '0');
        const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const mes = meses[d.getMonth()];
        const anio = d.getFullYear();
        return `${dia} ${mes} ${anio}`;
      }
    } catch {
      // Fallback
    }
    return String(fecha);
  }

  /**
   * Retorna el subtítulo de carga con fecha y usuario si existe
   */
  get subtituloCarga(): string {
    const prefijo = this.navService.buscarMensaje('MATRIXADJUNTOSMODAL.LBL_CARGADO_FECHA') || 'Cargado:';
    const fecha = this.formatearFecha(this.adjunto?.fechaAdjunto);
    return `${prefijo} ${fecha}`;
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private limpiar(): void {
    this.archivoBlob = null;
    this.fileUrl = null;
    this.nombreArchivo = '';
    this.usuarioCarga = '';
    this.esPdf = false;
    this.loadingFile = false;
    this.guardandoCalificacion = false;
  }
}
